import { Anchor, Badge, Group, Table } from '@mantine/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { api, podatki } from '../api/odjemalec';
import { datum, datumZaApi, denar } from '../oblika/oblika';
import { Obdobje } from '../seznam/Filtri';
import { GlavaZaslona, IskalnoPolje, Seznam, Stolpec, ZaslonSeznama } from '../seznam/Seznam';
import { useSeznam } from '../seznam/useSeznam';

/** Ključi razvrščanja, ki jih sprejme strežnik (RacuniKontroler, storitev.Strani.razvrscanje). */
type Razvrsti = 'stevilka' | 'datum' | 'potek' | 'znesek' | 'kupec' | 'uporabnik';

/**
 * Pregled računov (samo branje): iskanje in obdobje filtrirata na strežniku,
 * klik na glavo stolpca razvršča (naraščajoče → padajoče → privzeto), strani po 25/50/100/200. Stanje je v URL-ju.
 * Klik na vrstico odpre podrobnosti računa.
 */
export function PregledRacunov() {
  const seznam = useSeznam<Razvrsti>();
  const navigate = useNavigate();
  const od = seznam.parameter('od');
  const doDatuma = seznam.parameter('do');
  const poizvedba = useQuery({
    queryKey: ['racuni', seznam.iskanje, od, doDatuma, seznam.zaApi],
    queryFn: ({ signal }) =>
      podatki(
        api.GET('/api/racuni', {
          signal,
          params: {
            query: {
              iskanje: seznam.iskanje || undefined,
              od: datumZaApi(od),
              do: datumZaApi(doDatuma),
              ...seznam.zaApi,
            },
          },
        }),
      ),
    placeholderData: keepPreviousData,
  });
  const vrstice = poizvedba.data?.vsebina ?? [];

  return (
    <ZaslonSeznama>
      <GlavaZaslona naslov="Računi" opis="Pregled izdanih računov." />
      <Seznam
        seznam={seznam}
        filtri={
          <>
            <IskalnoPolje seznam={seznam} namig="Išči po kupcu, davčni številki, naslovu, številki računa ali znesku" />
            <Obdobje seznam={seznam} />
          </>
        }
        glava={
          <>
            <Stolpec seznam={seznam} kljuc="stevilka" naziv="Št. računa" />
            <Stolpec seznam={seznam} kljuc="datum" naziv="Ustvarjen" />
            <Stolpec seznam={seznam} kljuc="potek" naziv="Potek" />
            <Stolpec seznam={seznam} kljuc="znesek" naziv="Znesek" desno />
            <Stolpec seznam={seznam} kljuc="kupec" naziv="Kupec" />
            <Stolpec seznam={seznam} kljuc="uporabnik" naziv="Uporabnik" />
          </>
        }
        vrstice={vrstice.map((r, i) => (
          <Table.Tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => void navigate(`/racuni/${r.id}`)}>
            <Table.Td c="dimmed" className="brez-preloma">
              {seznam.zamik + i + 1}
            </Table.Td>
            <Table.Td>
              <Group gap={6} wrap="nowrap">
                <Anchor
                  component={Link}
                  to={`/racuni/${r.id}`}
                  onClick={(e) => e.stopPropagation()}
                  fw={600}
                  className="brez-preloma"
                >
                  {r.stRacuna}
                </Anchor>
                {r.storniran && <Oznaka barva="red">storniran</Oznaka>}
                {r.dobropis && <Oznaka barva="orange">dobropis</Oznaka>}
              </Group>
            </Table.Td>
            <Table.Td className="brez-preloma">{datum(r.datum)}</Table.Td>
            <Table.Td className="brez-preloma">{datum(r.datumPlacila)}</Table.Td>
            <Table.Td ta="right" className="brez-preloma">
              {denar(r.znesek)}
            </Table.Td>
            <Table.Td>{r.kupec}</Table.Td>
            <Table.Td>{r.uporabnikVozila}</Table.Td>
          </Table.Tr>
        ))}
        stVrstic={vrstice.length}
        stStolpcev={6}
        skupaj={poizvedba.data?.skupaj ?? 0}
        nalaganje={poizvedba.isPending}
        osvezevanje={poizvedba.isFetching}
        napaka={poizvedba.error}
        ponovi={() => void poizvedba.refetch()}
        prazno="Ni računov za izbrane pogoje."
        napakaNaslov="Računov ni bilo mogoče naložiti"
      />
    </ZaslonSeznama>
  );
}

export function Oznaka({ barva, children }: { barva: string; children: ReactNode }) {
  return (
    <Badge size="xs" variant="light" color={barva} radius="sm">
      {children}
    </Badge>
  );
}
